import { FlexibleConnectedPositionStrategy, Overlay, OverlayRef, ScrollStrategy } from '@angular/cdk/overlay';
import { NgZone } from '@angular/core';
import { fromEvent, Subscription } from 'rxjs';


/**
 * Stratégie d'overlay des panneaux d'autocomplétion du lazy-list-picker : le panneau reste
 * collé à son champ pendant tout défilement, et ne le recouvre jamais.
 *
 * Deux défauts de la configuration par défaut de Kaligraphi sont compensés ici, sans la modifier :
 * - sa stratégie de scroll s'appuie sur ScrollDispatcher, aveugle aux conteneurs overflow non
 *   enregistrés CdkScrollable (formulaire du requêteur) → panneau figé, détaché du champ.
 *   Remède : écoute capture-phase sur document + repositionnement à chaque défilement.
 * - sa stratégie de position n'a qu'une position (dessous) avec « push » CDK : quand la place
 *   manque sous le champ, le panneau est repoussé par-dessus le champ et ses voisins.
 *   Remède : position de repli au-dessus du champ et push désactivé (via updatePositionStrategy).
 *
 * Le panneau ne se ferme que lorsque le champ sort entièrement de la zone visible du conteneur
 * scrollé. Le défilement interne de la liste d'options est ignoré.
 */
export class LazyListPickerScrollStrategy implements ScrollStrategy {

    private scrollSubscription: Subscription | null = null;

    private overlayRef: OverlayRef | null = null;

    private positionStrategyReplaced = false;

    constructor(private readonly overlay: Overlay,
                private readonly ngZone: NgZone,
                private readonly document: Document,
                private readonly origin: () => HTMLElement | undefined) {
    }

    attach(overlayRef: OverlayRef): void {
        this.overlayRef = overlayRef;
    }

    detach(): void {
        this.disable();
        this.overlayRef = null;
        this.positionStrategyReplaced = false;
    }

    disable(): void {
        this.scrollSubscription?.unsubscribe();
        this.scrollSubscription = null;
    }

    enable(): void {
        this.replacePositionStrategyOnce();

        if (this.scrollSubscription || !this.document) {
            return;
        }

        // Hors zone Angular : updatePosition() ne touche que le DOM, on ne rentre dans la
        // zone que pour fermer.
        this.scrollSubscription = this.ngZone.runOutsideAngular(() =>
            fromEvent<Event>(this.document, 'scroll', {capture: true, passive: true})
                .subscribe(event => this.onScroll(event.target as Node))
        );
    }

    /**
     * Remplacement fait à l'activation et non dans attach() : le constructeur d'OverlayRef
     * attache la stratégie de scroll PUIS affecte la stratégie de position de la config,
     * ce qui écraserait un remplacement effectué dans attach().
     */
    private replacePositionStrategyOnce(): void {
        if (this.positionStrategyReplaced || !this.overlayRef) {
            return;
        }

        const origin = this.origin();
        if (!origin) {
            return;
        }

        this.overlayRef.updatePositionStrategy(this.buildPositionStrategy(origin));
        this.positionStrategyReplaced = true;
    }

    /** Panneau sous le champ, sinon au-dessus ; jamais repoussé par-dessus (pas de push CDK). */
    private buildPositionStrategy(origin: HTMLElement): FlexibleConnectedPositionStrategy {
        return this.overlay.position()
            .flexibleConnectedTo(origin)
            .withFlexibleDimensions(false)
            .withPush(false)
            .withPositions([
                {originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top'},
                {originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom'}
            ]);
    }

    private onScroll(target: Node): void {
        if (!this.overlayRef?.hasAttached() || this.isScrollInsidePanel(target)) {
            return;
        }

        const origin = this.origin();
        if (origin && target && !target.contains(origin)) {
            return;
        }

        if (origin && this.isOriginOutOfView(origin, target)) {
            // La fermeture déclenche des mises à jour applicatives → retour dans la zone Angular.
            this.ngZone.run(() => this.overlayRef.detach());
            return;
        }

        this.overlayRef.updatePosition();
    }

    /** Défilement interne de la liste d'options : sans effet sur la position du champ. */
    private isScrollInsidePanel(target: Node): boolean {
        const panel = this.overlayRef.overlayElement;
        return !!target && !!panel && (panel === target || panel.contains(target));
    }

    /** Vrai quand le champ est entièrement sorti de la zone visible du conteneur scrollé (ou de la fenêtre). */
    private isOriginOutOfView(origin: HTMLElement, target: Node): boolean {
        const originRect = origin.getBoundingClientRect();
        let top = 0;
        let bottom = this.document.defaultView?.innerHeight ?? 0;
        if (target instanceof Element) {
            const targetRect = target.getBoundingClientRect();
            top = targetRect.top;
            bottom = targetRect.bottom;
        }
        return originRect.bottom < top || originRect.top > bottom;
    }
}
